import { useCallback, useLayoutEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// backdropContrast
// ---------------------------------------------------------------------------
//
// Pick a foreground colour ("white" or "black") that has the highest contrast
// against whatever is painted *behind* a given element, defaulting to white.
//
// Used by the editor's "+ Add Image" / "+ Add Text" placeholders, which float
// on top of arbitrary user content (solid colours, gradients, photos). We want
// the dashed rectangle + label to stay readable without the user configuring
// anything, so we sample the nearest solid ancestor background and decide via
// the WCAG relative-luminance crossover.
//
// Why white by default: a backdrop we cannot measure is almost always a photo
// or gradient (hero/section media), and white reads well on the typical
// mid-to-dark imagery used for backgrounds. White is therefore the safe
// fallback whenever the backdrop is an image, a gradient, transparent, or
// otherwise undetectable.

// The WCAG contrast ratios of white (L=1) and black (L=0) text against a
// backdrop of relative luminance L are:
//   white: (1 + 0.05) / (L + 0.05)
//   black: (L + 0.05) / (0 + 0.05)
// They are equal when (L + 0.05)^2 = 1.05 * 0.05, i.e. L ≈ 0.1791. Below the
// crossover white wins; above it black wins.
export const WHITE_TEXT_LUMINANCE_CROSSOVER = Math.sqrt(1.05 * 0.05) - 0.05;

/**
 * Parse a CSS colour string (``rgb()``/``rgba()`` as returned by
 * ``getComputedStyle``, or a ``#hex``) into ``{ r, g, b, a }`` with channels in
 * 0–255 and alpha in 0–1. Returns ``null`` for anything we can't read
 * (``transparent``, ``none``, named colours, empty strings).
 */
export function parseCssColor(input) {
  if (!input || typeof input !== "string") return null;
  const value = input.trim().toLowerCase();
  if (!value || value === "transparent" || value === "none") return null;

  const rgbMatch = value.match(
    /^rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/,
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    let a = 1;
    if (rgbMatch[4] != null) {
      a = rgbMatch[4].endsWith("%")
        ? Number(rgbMatch[4].slice(0, -1)) / 100
        : Number(rgbMatch[4]);
    }
    if ([r, g, b, a].some((n) => !Number.isFinite(n))) return null;
    return { r, g, b, a };
  }

  const hexMatch = value.match(/^#([0-9a-f]{3,8})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length !== 6 && hex.length !== 8) return null;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return { r, g, b, a };
  }

  return null;
}

/** WCAG relative luminance (0–1) for an ``{ r, g, b }`` colour (0–255). */
export function relativeLuminance({ r, g, b }) {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Return "white" or "black" — whichever has more contrast at luminance ``L``. */
export function bestTextColorForLuminance(L) {
  return L < WHITE_TEXT_LUMINANCE_CROSSOVER ? "white" : "black";
}

// A background-color only "covers" what's below it once it's mostly opaque.
// Below this we keep walking up to the next painted layer.
const MIN_COVERING_ALPHA = 0.5;

/**
 * Walk up the ancestor chain from ``el`` and decide the best readable text
 * colour ("white"/"black") for a placeholder drawn on top of it.
 *
 * - The first ancestor with a (non-``none``) ``background-image`` (this also
 *   covers CSS gradients) short-circuits to the ``fallback`` — we can't cheaply
 *   sample an image's luminance, and white is the safe default.
 * - The first ancestor with a sufficiently opaque ``background-color`` decides
 *   the colour from its luminance.
 * - Reaching the document root with neither → ``fallback``.
 */
export function resolveBackdropTextColor(el, { fallback = "white" } = {}) {
  if (!el || typeof window === "undefined" || !window.getComputedStyle) {
    return fallback;
  }

  let node = el;
  while (node && node.nodeType === 1) {
    let cs;
    try {
      cs = window.getComputedStyle(node);
    } catch {
      return fallback;
    }
    if (cs) {
      const bgImage = cs.backgroundImage;
      if (bgImage && bgImage !== "none") {
        return fallback;
      }
      const color = parseCssColor(cs.backgroundColor);
      if (color && color.a >= MIN_COVERING_ALPHA) {
        return bestTextColorForLuminance(relativeLuminance(color));
      }
    }
    node = node.parentElement;
  }
  return fallback;
}

/**
 * React hook: resolve the readable text colour for a placeholder. Returns
 * ``"white"`` (default) or ``"black"``. Recomputes on mount and exposes a
 * ``remeasure`` callback callers can fire on hover (when the affordance becomes
 * visible) so a freshly-edited backdrop is re-sampled.
 */
export function useBackdropTextColor(ref, { fallback = "white" } = {}) {
  const [textColor, setTextColor] = useState(fallback);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const remeasure = useCallback(() => {
    const el = ref?.current;
    if (!el) return;
    const next = resolveBackdropTextColor(el, { fallback: fallbackRef.current });
    setTextColor((prev) => (prev === next ? prev : next));
  }, [ref]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const raf = window.requestAnimationFrame
      ? window.requestAnimationFrame(remeasure)
      : (remeasure(), null);
    return () => {
      if (raf != null && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(raf);
      }
    };
  }, [remeasure]);

  return { textColor, remeasure };
}
